import kfetch from '@kareo/kfetch';

import availability from './availability';
import practiceUsers from './practiceUsers';

// "Engage" is the product formerly known as Kareo Marketing formerly known as Kareo Office.
export const ENGAGE_PRODUCT_KEY = 'KAREO_OFFICE';
export const TELHEALTH_PRODCUT_KEY = 'KAREO_TELEHEALTH';

export class Providers {
  constructor() {
    this.instance = null;
    this.providerPromise = null;
  }

  /**
   * @param {object} config
   * @param {object} config.signal Abort controller signal
   * @returns {Promise<Array<any>>}
   */
  getAllProviders(config = {}) {
    if (!this) {
      return Promise.reject(
        new Error(
          'unknown local scope in getAllProviders, cant define promise',
        ),
      );
    }

    if (!this.providerPromise) {
      this.providerPromise = kfetch('/admin-ui/api/Provider/all', config);
    }

    return this.providerPromise.then(providerResponse => {
      return providerResponse.sort((a, b) => {
        let aName = `${a.firstName} ${a.middleName} ${a.lastName}`;
        let bName = `${b.firstName} ${a.middleName} ${b.lastName}`;

        return aName.localeCompare(bName, undefined, {
          numeric: true,
        });
      });
    });
  }

  async getProviderByGuid(providerGuid, options) {
    if (!!providerGuid) {
      let response = await kfetch(`/admin-ui/api/Provider/${providerGuid}`, {
        method: 'GET',
        ...options,
      });
      return response;
    }
    return null;
  }

  async checkAvailabilityBatch(
    providerIds,
    appointmentStartTime,
    appointmentDuration,
    recurrenceRules,
    abortSignal,
  ) {
    const basePayload = availability.createAvailabilityPayload(
      appointmentStartTime,
      appointmentDuration,
      recurrenceRules,
    );

    return kfetch('/dashboard-calendar-ui/api/Appointment/conflictsBatch', {
      method: 'POST',
      body: JSON.stringify({
        providerIds,
        ...basePayload,
      }),
      ...abortSignal,
    });
  }

  async checkAvailability(
    providerIds,
    appointmentId,
    appointmentStartTime,
    appointmentDuration,
    recurrenceRules,
    abortSignal,
  ) {
    let basePayload = availability.createAvailabilityPayload(
      appointmentStartTime,
      appointmentDuration,
      recurrenceRules,
    );

    let availabilityPromises = providerIds.map(providerId => {
      let payload = {
        providerId,
        ...basePayload,
      };

      return availability
        .checkAvailability(payload, abortSignal, appointmentId)
        .then(response => ({ providerId, status: response }));
    });

    let results = await Promise.all(availabilityPromises);

    return results;
  }

  async getAllProviderUsers(options = {}) {
    if (!this.providerUsers) {
      let users = await practiceUsers.getAllPracticeUsers(options);
      let providerUsers = users.filter(user => !!user.providerInfo);

      this.providerUsers = providerUsers;
    }

    return this.providerUsers;
  }

  static shapeProviderProduct(providerUsers) {
    return providerUsers.reduce((acc, providerUserInfo) => {
      if (
        !!providerUserInfo &&
        providerUserInfo.providerInfo &&
        providerUserInfo.providerInfo.providerGuid
      ) {
        acc[providerUserInfo.providerInfo.providerGuid] =
          providerUserInfo.providerInfo.products;
      }

      return acc;
    }, {});
  }

  async getProviderProducts(providerGuid, options = {}) {
    if (!this.providerProducts) {
      let providerUsers = await this.getAllProviderUsers(options);

      let providerProducts = Providers.shapeProviderProduct(providerUsers);

      this.providerProducts = providerProducts;
    }

    return !!providerGuid
      ? { ...this.providerProducts[providerGuid] }
      : { ...this.providerProducts };
  }

  /**
   * check if provider has engage product
   * @param {string} providerGuid - provider unique identifier
   * @param {Object} options - options passed to kfetch
   * @returns {Promise<Boolean>}
   */
  async providerHasEngage(providerGuid, options = {}) {
    let hasEngage = await this.providerHasProducts(
      providerGuid,
      [ENGAGE_PRODUCT_KEY],
      options,
    );

    return hasEngage[0];
  }

  /**
   * check if provider has telehealth product
   * @param {string} providerGuid - provider unique identifier
   * @param {Object} options - options passed to kfetch
   * @returns {Promise<Boolean>}
   */
  async providerHasTelehealth(providerGuid, options = {}) {
    let hasTelehealth = await this.providerHasProducts(
      providerGuid,
      [TELHEALTH_PRODCUT_KEY],
      options,
    );
    return hasTelehealth[0];
  }

  /**
   * checks for exists of a key on an object and returns the value
   * @param {Object} obj
   * @param {string} key
   * @returns {Boolean | any}
   */
  static hasKey = (obj, key) => {
    return (
      !!obj && typeof obj === 'object' && !!obj.hasOwnProperty(key) && obj[key]
    );
  };
  /**
   * checks multiple product keys
   * @param {string} providerGuid
   * @param {array} productList array of product keys, should be a constant above
   * @param {object} options
   * @returns {Promise<Boolean>}
   */
  async providerHasProducts(providerGuid, productList, options = {}) {
    let providerProducts = await this.getProviderProducts(
      providerGuid,
      options,
    );

    return productList.map(productKey => {
      return Providers.hasKey(providerProducts, productKey);
    });
  }

  /**
   *
   * @param {Array | string} productList
   * @param {object} options
   * @returns boolean
   */
  async anyProviderHasProduct(productList, options = {}) {
    if (!productList) return null;
    let providerProducts = await this.getProviderProducts(null, options);

    let allSubscribedProducts = Object.values(providerProducts);

    if (typeof productList === 'string') {
      productList = [productList];
    }

    // look through the products List to determine if any provider has the product subscription value turned on
    let aProviderhasProduct = productList.find(productListItem =>
      allSubscribedProducts.find(providerProductSubscription =>
        Providers.hasKey(providerProductSubscription, productListItem),
      ),
    );

    return !!aProviderhasProduct;
  }

  static get() {
    if (!this.instance) {
      this.instance = new Providers();
    }

    return this.instance;
  }
}

// Export a single instance of the Providers class
const providers = Providers.get();

export default providers;
